import React from 'react'
import styled, { keyframes } from 'styled-components'
import moment from 'moment'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import AssignmentIcon from '@mui/icons-material/Assignment'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import PersonIcon from '@mui/icons-material/Person'
import GavelIcon from '@mui/icons-material/Gavel'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import GroupsIcon from '@mui/icons-material/Groups'
import { PRIVATE_PATHS, redirect } from 'utils/paths'

const TaskDashboard = ({ tasks, currentUser, onSwitchView, onSelectCategory, serverStats, teams = [] }) => {
  const overdueTasks = tasks.filter(t => t.isOverdue && t.status !== 'completed')
  const pendingApproval = tasks.filter(t => t.status === 'awaiting_approval')
  const dueSoon = tasks.filter(t => {
    if (!t.deadline || t.status === 'completed') return false
    const diff = moment(t.deadline).diff(moment(), 'days')
    return diff > 0 && diff <= 7
  })

  const stats = [
    {
      label: 'My Open Tasks',
      value: serverStats?.myOpenTasks ?? 0,
      icon: PersonIcon,
      color: '#33becc',
      bg: '#e6f7f9',
      action: () => onSelectCategory('my_tasks')
    },
    {
      label: 'Overdue',
      value: serverStats?.overdue ?? 0,
      icon: WarningAmberIcon,
      color: '#dc2626',
      bg: '#fee2e2',
      action: () => onSelectCategory('overdue')
    },
    {
      label: 'Pending Approval',
      value: serverStats?.pendingApproval ?? 0,
      icon: GavelIcon,
      color: '#7c3aed',
      bg: '#ede9fe',
      action: () => onSelectCategory('awaiting_approval')
    },
    {
      label: 'Completed Today',
      value: serverStats?.completedToday ?? 0,
      icon: CheckCircleOutlineIcon,
      color: '#059669',
      bg: '#d1fae5',
      action: () => onSelectCategory('completed')
    },
    {
      label: 'In Progress',
      value: serverStats?.inProgress ?? 0,
      icon: TrendingUpIcon,
      color: '#0891b2',
      bg: '#cffafe',
      action: () => onSwitchView('progress-tracker')
    },
    {
      label: 'On Hold',
      value: serverStats?.onHold ?? 0,
      icon: PauseCircleOutlineIcon,
      color: '#d97706',
      bg: '#fef3c7',
      action: () => { onSwitchView('progress-tracker'); onSelectCategory('on_hold') }
    },
    {
      label: 'Due This Week',
      value: serverStats?.dueThisWeek ?? 0,
      icon: CalendarTodayIcon,
      color: '#d97706',
      bg: '#fef3c7',
      action: () => onSelectCategory('due_this_week')
    }
  ]

  return (
    <DashboardWrapper>
      <WelcomeSection>
        <WelcomeText>
          Welcome back{currentUser?.firstName ? `, ${currentUser.firstName}` : ''}
        </WelcomeText>
        <WelcomeSubtext>
          Here's an overview of your task management activity.
        </WelcomeSubtext>
      </WelcomeSection>

      <StatsGrid>
        {stats.map((stat, i) => {
          const Icon = stat.icon
          return (
            <StatCard key={i} onClick={stat.action} $index={i}>
              <StatIcon $color={stat.color} $bg={stat.bg}>
                <Icon />
              </StatIcon>
              <StatInfo>
                <StatValue $color={stat.value > 0 && stat.color === '#dc2626' ? '#dc2626' : undefined}>
                  {stat.value}
                </StatValue>
                <StatLabel>{stat.label}</StatLabel>
              </StatInfo>
            </StatCard>
          )
        })}
      </StatsGrid>

      <SectionsRow>
        <Section>
          <SectionHeader>
            <SectionTitle>
              <WarningAmberIcon style={{ color: '#dc2626', fontSize: 20 }} />
              Overdue Tasks
            </SectionTitle>
            {overdueTasks.length > 0 && (
              <SectionLink onClick={() => onSelectCategory('overdue')}>
                View all <ArrowForwardIcon />
              </SectionLink>
            )}
          </SectionHeader>
          <SectionContent>
            {overdueTasks.length === 0 ? (
              <EmptySection>No overdue tasks</EmptySection>
            ) : (
              overdueTasks.slice(0, 5).map(task => (
                <TaskItem key={task.id} $overdue $domainColour={task.taskDomain?.colour} onClick={() => redirect(PRIVATE_PATHS.taskDetails.replace(':id', task.id))}>
                  <TaskItemTitle>{task.title}</TaskItemTitle>
                  <TaskItemMeta>
                    <TaskItemMetaLeft>
                      {task.taskDomain && (
                        <DomainChip $colour={task.taskDomain.colour}>
                          <DomainSwatch $colour={task.taskDomain.colour} />
                          <span>{task.taskDomain.short || task.taskDomain.name}</span>
                        </DomainChip>
                      )}
                      <span>{task.building?.name}</span>
                    </TaskItemMetaLeft>
                    <TaskItemDeadline $overdue>
                      {Math.abs(moment(task.deadline).diff(moment(), 'days')) || '< 1'}d overdue
                    </TaskItemDeadline>
                  </TaskItemMeta>
                </TaskItem>
              ))
            )}
          </SectionContent>
        </Section>

        <Section>
          <SectionHeader>
            <SectionTitle>
              <GavelIcon style={{ color: '#7c3aed', fontSize: 20 }} />
              Pending Your Approval
            </SectionTitle>
            {pendingApproval.length > 0 && (
              <SectionLink onClick={() => onSelectCategory('awaiting_approval')}>
                View all <ArrowForwardIcon />
              </SectionLink>
            )}
          </SectionHeader>
          <SectionContent>
            {pendingApproval.length === 0 ? (
              <EmptySection>No tasks awaiting approval</EmptySection>
            ) : (
              pendingApproval.slice(0, 5).map(task => (
                <TaskItem key={task.id} $domainColour={task.taskDomain?.colour} onClick={() => redirect(PRIVATE_PATHS.taskDetails.replace(':id', task.id))}>
                  <TaskItemTitle>{task.title}</TaskItemTitle>
                  <TaskItemMeta>
                    <TaskItemMetaLeft>
                      {task.taskDomain && (
                        <DomainChip $colour={task.taskDomain.colour}>
                          <DomainSwatch $colour={task.taskDomain.colour} />
                          <span>{task.taskDomain.short || task.taskDomain.name}</span>
                        </DomainChip>
                      )}
                      <span>{task.building?.name}</span>
                    </TaskItemMetaLeft>
                    {task.deadline && (
                      <TaskItemDeadline>
                        Due {moment(task.deadline).format('DD MMM YYYY')}
                      </TaskItemDeadline>
                    )}
                  </TaskItemMeta>
                </TaskItem>
              ))
            )}
          </SectionContent>
        </Section>

        <Section>
          <SectionHeader>
            <SectionTitle>
              <CalendarTodayIcon style={{ color: '#d97706', fontSize: 20 }} />
              Due This Week
            </SectionTitle>
          </SectionHeader>
          <SectionContent>
            {dueSoon.length === 0 ? (
              <EmptySection>No tasks due this week</EmptySection>
            ) : (
              dueSoon.slice(0, 5).map(task => (
                <TaskItem key={task.id} $domainColour={task.taskDomain?.colour} onClick={() => redirect(PRIVATE_PATHS.taskDetails.replace(':id', task.id))}>
                  <TaskItemTitle>{task.title}</TaskItemTitle>
                  <TaskItemMeta>
                    <TaskItemMetaLeft>
                      {task.taskDomain && (
                        <DomainChip $colour={task.taskDomain.colour}>
                          <DomainSwatch $colour={task.taskDomain.colour} />
                          <span>{task.taskDomain.short || task.taskDomain.name}</span>
                        </DomainChip>
                      )}
                      <span>{task.building?.name}</span>
                    </TaskItemMetaLeft>
                    <TaskItemDeadline>
                      {moment(task.deadline).format('ddd, DD MMM YYYY')}
                    </TaskItemDeadline>
                  </TaskItemMeta>
                </TaskItem>
              ))
            )}
          </SectionContent>
        </Section>
      </SectionsRow>

      <QuickActions>
        <QuickAction onClick={() => redirect(PRIVATE_PATHS.createTask)}>
          <AssignmentIcon />
          <span>Create Task</span>
        </QuickAction>
        <QuickAction onClick={() => onSwitchView('progress-tracker')}>
          <TrendingUpIcon />
          <span>Progress Tracker</span>
        </QuickAction>
        <QuickAction onClick={() => onSwitchView('table')}>
          <AccessTimeIcon />
          <span>Table View</span>
        </QuickAction>
      </QuickActions>

      {teams.length > 0 && (
        <TeamsSection>
          <SectionHeader>
            <SectionTitle>
              <GroupsIcon style={{ color: '#0d7377', fontSize: 20 }} />
              My Teams
            </SectionTitle>
          </SectionHeader>
          <TeamsGrid>
            {teams.map(team => {
              const teamStats = serverStats?.teams?.find(t => t.id === team.id)
              return (
                <TeamCard key={team.id} onClick={() => onSelectCategory(`team_${team.id}`)}>
                  <TeamCardName>{team.name}</TeamCardName>
                  <TeamCardMeta>
                    {team.memberCount || 0} member{(team.memberCount || 0) !== 1 ? 's' : ''}
                    {teamStats && teamStats.openTaskCount > 0 && (
                      <TeamCardTasks>
                        {teamStats.openTaskCount} open task{teamStats.openTaskCount !== 1 ? 's' : ''}
                      </TeamCardTasks>
                    )}
                  </TeamCardMeta>
                </TeamCard>
              )
            })}
          </TeamsGrid>
        </TeamsSection>
      )}
    </DashboardWrapper>
  )
}

export default TaskDashboard

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`

const DashboardWrapper = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
  animation: ${fadeIn} 0.3s ease-out;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const WelcomeSection = styled.div`
  margin-bottom: 28px;
`

const WelcomeText = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0 0 4px 0;
`

const WelcomeSubtext = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin: 0;
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 28px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: all 0.2s;
  animation: ${fadeIn} 0.3s ease-out;
  animation-delay: ${({ $index }) => $index * 50}ms;
  animation-fill-mode: backwards;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 16px;
    gap: 12px;
  }
`

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${({ $bg }) => $bg};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 24px;
    color: ${({ $color }) => $color};
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    border-radius: 10px;

    svg { font-size: 20px; }
  }
`

const StatInfo = styled.div`
  min-width: 0;
`

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${({ $color, theme }) => $color || theme.colors.haiti};
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`

const StatLabel = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin-top: 2px;
`

const SectionsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 28px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`

const Section = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
`

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const SectionLink = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;

  svg { font-size: 14px; }

  &:hover { text-decoration: underline; }
`

const SectionContent = styled.div`
  max-height: 300px;
  overflow-y: auto;
`

const EmptySection = styled.div`
  padding: 32px 20px;
  text-align: center;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
`

const TaskItem = styled.div`
  padding: 12px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  cursor: pointer;
  transition: background 0.15s;
  ${({ $overdue, $domainColour }) => $overdue
    ? 'border-left: 3px solid #dc2626;'
    : ($domainColour && `border-left: 3px solid ${$domainColour};`)}

  &:last-child { border-bottom: none; }
  &:hover { background: ${({ theme }) => theme.colors.lightGrey}; }
`

const TaskItemTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const TaskItemMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
`

const TaskItemMetaLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const DomainChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2px;
  flex-shrink: 0;
  color: ${({ $colour }) => $colour || '#475569'};
  background-color: ${({ $colour }) => `${$colour || '#64748b'}14`};
`

const DomainSwatch = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${({ $colour }) => $colour || '#64748b'};
`

const TaskItemDeadline = styled.span`
  font-weight: 500;
  color: ${({ $overdue }) => $overdue ? '#dc2626' : 'inherit'};
`

const TeamsSection = styled.div`
  margin-top: 28px;
  background: white;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  margin-bottom: 28px;
`

const TeamsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
  padding: 16px 20px;
`

const TeamCard = styled.div`
  padding: 16px;
  border-radius: 10px;
  background: #f0fdfd;
  border: 1px solid #d1eeee;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 2px 8px rgba(13, 115, 119, 0.15);
    transform: translateY(-1px);
  }
`

const TeamCardName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 4px;
`

const TeamCardMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  display: flex;
  align-items: center;
  gap: 8px;
`

const TeamCardTasks = styled.span`
  color: #0d7377;
  font-weight: 500;
`

const QuickActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`

const QuickAction = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
  transition: all 0.2s;

  svg {
    font-size: 20px;
    color: ${({ theme }) => theme.colors.secondary};
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.secondary};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
`
